<script setup lang="ts">
import Chart from '@/views/scrape-items/parts/chart.vue'

const props = defineProps<{
	scrapeItem: App.Data.ScrapeItem.ScrapeItemData
	chartData: App.Data.ScrapeItem.Chart.ChartData
}>()

const { copy, isSupported } = useClipboard({ legacy: true })
</script>


					<script lang="ts">
					import __hybridly_layout_0 from '~/resources/layouts/default.vue';
					export default { layout: [__hybridly_layout_0] }
					</script>
					<template>
				
	<content-wrapper>
		<template v-if="props.scrapeItem.last_processed_at">
			<div class="flex items-center gap-4">
				<div v-if="props.scrapeItem.image" class="bg-sidebar size-26 shrink-0 rounded-md p-1">
					<img
						:src="props.scrapeItem.image ?? ''"
						:alt="props.scrapeItem.name ?? ''"
						class="aspect-square object-contain object-center mix-blend-multiply"
						loading="lazy"
						onerror="this.style.display='none'"
					/>
				</div>
				<h2 class="flex gap-1 text-xl font-bold">
					{{ props.scrapeItem.name }}
					<template v-if="isSupported">
						<ui-tooltip>
							<ui-tooltip-trigger>
								<i-ph-link-bold class="text-primary size-5 cursor-pointer" @click="copy(props.scrapeItem.full_url)" />
							</ui-tooltip-trigger>
							<ui-tooltip-content>
								{{ t('app.buttons.copy_url') }}
							</ui-tooltip-content>
						</ui-tooltip>
					</template>
				</h2>
			</div>

			<div class="mt-4 flex flex-col gap-4 lg:flex-row">
				<div class="grow">
					<chart :chart-data="chartData" />
				</div>
				<div class="lg:min-w-md flex flex-col gap-1 lg:border-l lg:pl-4">
					<h3 class="text-lg font-bold">
						{{ props.scrapeItem.team?.name }}
					</h3>

					<div class="mt-0.5 flex items-center gap-1">
						{{ props.scrapeItem.latest_result?.price }}
						<div v-if="props.scrapeItem.is_cheapest" class="bg-successful size-2 rounded-full" />
						<div v-else-if="props.scrapeItem.latest_result?.price" class="bg-destructive size-2 rounded-full" />
					</div>

					<availability v-if="props.scrapeItem.latest_result?.availability" :availability="props.scrapeItem.latest_result?.availability" />

					<div class="text-muted-foreground flex items-center gap-1 text-xs">
						<i-ph-arrows-clockwise-bold />
						{{ scrapeItem.last_processed_at }}
						<template v-if="scrapeItem.error_description">
							<ui-tooltip>
								<ui-tooltip-trigger>
									<i-ph-warning-bold class="text-destructive size-4 cursor-pointer" />
								</ui-tooltip-trigger>
								<ui-tooltip-content class="bg-destructive">
									{{ scrapeItem.error_description }}
								</ui-tooltip-content>
							</ui-tooltip>
						</template>
					</div>
				</div>
			</div>

			<div class="mt-4 flex flex-wrap gap-3">
				<div v-for="childScrapeItem in scrapeItem.children" :key="childScrapeItem.uuid" class="bg-sidebar rounded-md p-2 lg:w-60">
					<div class="mb-1 flex items-center gap-2 border-b pb-1 text-sm">
						<ui-avatar-initials :name="childScrapeItem.competitor?.name ?? ''" class="size-8 rounded-md" :style="{ '--avatar-background': childScrapeItem.competitor?.brand_color_foreground, '--avatar-foreground': childScrapeItem.competitor?.brand_color }" />
						<strong>{{ childScrapeItem.competitor?.name }}</strong>
						<template v-if="isSupported">
							<ui-tooltip>
								<ui-tooltip-trigger>
									<i-ph-link-bold class="text-primary size-4 cursor-pointer" @click="copy(childScrapeItem.full_url)" />
								</ui-tooltip-trigger>
								<ui-tooltip-content>
									{{ t('app.buttons.copy_url') }}
								</ui-tooltip-content>
							</ui-tooltip>
						</template>
					</div>

					<div v-if="childScrapeItem.last_processed_at" class="flex flex-col gap-1">
						<div class="flex flex-col">
							<div class="flex flex-wrap justify-between gap-2">
								<div class="flex items-center gap-1">
									{{ childScrapeItem.latest_result?.price }}
									<div v-if="childScrapeItem.is_cheapest" class="bg-successful size-2 rounded-full" />
								</div>
								<availability v-if="childScrapeItem.latest_result?.availability" :availability="childScrapeItem.latest_result?.availability" />
							</div>
						</div>

						<div class="text-muted-foreground flex items-center gap-1 text-xs">
							<i-ph-arrows-clockwise-bold />
							{{ childScrapeItem.last_processed_at }}
							<template v-if="childScrapeItem.error_description">
								<ui-tooltip>
									<ui-tooltip-trigger>
										<i-ph-warning-bold class="text-destructive size-4 cursor-pointer" />
									</ui-tooltip-trigger>
									<ui-tooltip-content class="bg-destructive">
										{{ childScrapeItem.error_description }}
									</ui-tooltip-content>
								</ui-tooltip>
							</template>
						</div>
					</div>
					<div v-else class="flex items-center gap-2">
						<i-ph-spinner-gap-bold class="text-primary size-6 animate-spin" />
						<span class="text-muted-foreground text-xs">
							{{ t('app.scrape_item_not_processed') }}
						</span>
					</div>
				</div>
			</div>
		</template>
		<template v-else>
			<div class="flex items-center gap-4">
				<div class="bg-sidebar size-26 flex shrink-0 items-center justify-center rounded-md p-1">
					<i-ph-spinner-gap-bold class="text-primary size-6 animate-spin" />
				</div>
				<h2 class="flex gap-1 text-xl font-bold">
					{{ props.scrapeItem.name }}
					<template v-if="isSupported">
						<ui-tooltip>
							<ui-tooltip-trigger>
								<i-ph-link-bold class="text-primary size-5 cursor-pointer" @click="copy(props.scrapeItem.full_url)" />
							</ui-tooltip-trigger>
							<ui-tooltip-content>
								{{ t('app.buttons.copy_url') }}
							</ui-tooltip-content>
						</ui-tooltip>
					</template>
				</h2>
			</div>

			<ui-alert variant="informative" class="mt-4">
				{{ t('app.scrape_item_not_processed') }}
			</ui-alert>
		</template>
	</content-wrapper>
</template>
